import React, { useRef, useState } from 'react';
import { X, Plus } from 'lucide-react';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// The API may hand the list back as a real array, a JSON-encoded array
// string, or a comma-separated string, under either the camelCase or
// snake_case key. Always returns a clean string[].
export const normalizeEmailList = (raw) => {
  if (raw == null || raw === '') return [];
  let list = raw;
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (s.startsWith('[')) {
      try {
        list = JSON.parse(s);
      } catch {
        list = s.split(',');
      }
    } else {
      list = s.split(',');
    }
  }
  if (!Array.isArray(list)) return [];
  return list
    .map((e) => (typeof e === 'string' ? e.trim().toLowerCase() : ''))
    .filter(Boolean);
};

export const pickEmailToFetchPdf = (obj) =>
  normalizeEmailList(obj?.emailToFetchPdf ?? obj?.email_to_fetch_pdf);

// Pulls a human-readable message out of an API error body, whatever shape
// the backend used (message / error / errors[] / errors{}).
export const extractApiErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (typeof data.message === 'string' && data.message) return data.message;
  if (typeof data.msg === 'string' && data.msg) return data.msg;
  if (typeof data.error === 'string' && data.error) return data.error;
  if (data.error && typeof data.error === 'object') return extractApiErrorMessage(data.error, fallback);
  if (data.data && typeof data.data === 'object') return extractApiErrorMessage(data.data, fallback);
  const errs = data.errors;
  if (Array.isArray(errs) && errs.length) {
    const first = errs[0];
    return typeof first === 'string' ? first : first?.message || first?.msg || fallback;
  }
  if (errs && typeof errs === 'object') {
    const first = Object.values(errs)[0];
    return Array.isArray(first) ? first[0] : typeof first === 'string' ? first : fallback;
  }
  return fallback;
};

// True when an API error is about the fetch-PDF email list — either the
// backend flagged the field explicitly, or the message mentions one of the
// submitted emails / the field itself.
export const isEmailToFetchPdfError = (data, message, emails = []) => {
  const field = String(data?.field ?? data?.key ?? data?.param ?? '').toLowerCase();
  if (field.includes('fetch') || field.includes('pdf')) return true;
  const errs = data?.errors;
  if (errs && typeof errs === 'object' && !Array.isArray(errs)) {
    if (Object.keys(errs).some((k) => /fetch|pdf/i.test(k))) return true;
  }
  const msg = String(message || '').toLowerCase();
  if (/fetch|pdf/.test(msg)) return true;
  return emails.some((e) => e && msg.includes(String(e).toLowerCase()));
};

// Chip-style multi-email input. `value` is the committed list of emails;
// whatever is typed but not yet committed lives only in local draft state.
// Enter / comma / blur / the + button commit the draft.
const EmailListInput = ({
  label = 'Email to Fetch PDF',
  value = [],
  onChange,
  error,
  compact = false,
}) => {
  const [draft, setDraft] = useState('');
  const [draftError, setDraftError] = useState('');
  const inputRef = useRef(null);

  // Splits typed/pasted text into candidate emails (comma/space separated).
  const parseDraft = (text) => {
    const parts = text
      .split(/[,\s]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    return { parts, invalid: parts.find((p) => !EMAIL_RE.test(p)) };
  };

  // Accepts one email or a pasted comma/space-separated list.
  const commit = () => {
    const { parts, invalid } = parseDraft(draft);
    if (!parts.length) {
      setDraft('');
      return;
    }
    if (invalid) {
      setDraftError(parts.length > 1 ? `"${invalid}" is not a valid email` : 'Enter a valid email address');
      return;
    }
    const next = [...value];
    let duplicate = null;
    parts.forEach((p) => {
      if (next.includes(p)) duplicate = p;
      else next.push(p);
    });
    if (next.length !== value.length) onChange(next);
    setDraft('');
    setDraftError(duplicate ? `${duplicate} is already added` : '');
  };

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  // Buttons inside the box must not steal focus from the input: otherwise
  // mousedown blurs the input, blur commits the draft, the box grows, the
  // centred modal shifts, and the click lands somewhere else.
  const keepFocus = (e) => e.preventDefault();

  // Clicking a chip pulls that email back into the input for editing. Any
  // valid text already in the input is committed first so it isn't lost.
  const editChip = (idx) => {
    const email = value[idx];
    let next = value.filter((_, i) => i !== idx);
    const { parts, invalid } = parseDraft(draft);
    if (parts.length && !invalid) {
      parts.forEach((p) => {
        if (!next.includes(p)) next.push(p);
      });
    }
    onChange(next);
    setDraft(email);
    setDraftError('');
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Backspace' && !draft && value.length) {
      remove(value.length - 1);
    }
  };

  const shownError = draftError || error;
  const boxPad = compact ? 'px-3 py-1.5' : 'px-4 py-2';
  const labelCls = compact ? 'font-medium mb-1' : 'font-semibold mb-2';

  return (
    <div>
      <label className={`block text-sm ${labelCls} text-gray-700 dark:text-gray-300`}>
        {label} <span className="text-red-500">*</span>
      </label>
      <div
        className={`flex flex-wrap items-center gap-2 ${boxPad} min-h-[46px] border ${
          shownError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
        } rounded-lg bg-white dark:bg-gray-700 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition`}
      >
        {value.map((email, idx) => (
          <span
            key={email}
            className="inline-flex items-center gap-1 pl-1 pr-1 py-0.5 rounded-md bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-200 text-sm"
          >
            <button
              type="button"
              onMouseDown={keepFocus}
              onClick={() => editChip(idx)}
              className="px-1.5 py-0.5 rounded hover:bg-blue-100 dark:hover:bg-blue-800 transition"
              title="Click to edit"
            >
              {email}
            </button>
            <button
              type="button"
              onMouseDown={keepFocus}
              onClick={() => remove(idx)}
              className="p-0.5 rounded hover:bg-blue-100 dark:hover:bg-blue-800 transition"
              aria-label={`Remove ${email}`}
            >
              <X size={14} />
            </button>
          </span>
        ))}
        <span className="flex flex-1 items-center min-w-[220px]">
          <input
            ref={inputRef}
            type="text"
            inputMode="email"
            autoComplete="off"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setDraftError('');
            }}
            onKeyDown={handleKeyDown}
            onBlur={commit}
            placeholder={value.length ? 'Add another email' : 'Type an email and press Enter'}
            className="flex-1 min-w-0 py-1 bg-transparent outline-none text-gray-900 dark:text-white placeholder-gray-400"
          />
          <button
            type="button"
            onMouseDown={keepFocus}
            onClick={commit}
            className="p-1.5 rounded-md text-blue-600 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/40 transition"
            title="Add email"
            aria-label="Add email"
          >
            <Plus size={16} />
          </button>
        </span>
      </div>
      {shownError ? (
        <p className="mt-1 text-xs text-red-500">{shownError}</p>
      ) : (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Press Enter or comma to add, click an email to edit it. At least one email is required.
        </p>
      )}
    </div>
  );
};

export default EmailListInput;
